import { forwardRef, useState } from "react";
import { tokens } from "@opendoor/tokens-studio/dist/bricks-next/tokens/Components";
import { Accordion as AccordionTamagui } from "@tamagui/accordion";
import { useProps } from "@tamagui/core";
import { ChevronDown } from "../../__generated__/icons";
import { ComponentWithViewTracking } from "../../helpers/analytics";
import { useObservabilitySafe } from "../../helpers/useObservabilitySafe";
import { Box } from "../Box";
import { Text } from "../Text";
import { HeightAnimator } from "./HeightAnimator";
import { jsx, jsxs } from "react/jsx-runtime";
const { accordion: accordionTokens } = tokens, Accordion = forwardRef(function({
  analyticsName,
  trackImpression = !1,
  inViewThreshold,
  showDivider,
  showBottomDivider = !0,
  isFullWidth,
  variant = "regular",
  disabled,
  items,
  titleAlign = "left",
  orientation = "top",
  defaultOpen = !1,
  ...propsIn
}, ref) {
  const { size = "medium" } = useProps(propsIn);
  let singleValue, multipleValue;
  defaultOpen && (items.length > 1 ? multipleValue = items.map((item) => item.title) : items.length > 0 && (singleValue = items[0].title));
  const accordionItems = items?.map((accordionItem, idx) => /* @__PURE__ */ jsx(
    AccordionItem,
    {
      showDivider: idx !== items.length - 1 ? showDivider : showDivider && showBottomDivider,
      isFullWidth,
      disabled,
      variant,
      size,
      title: accordionItem.title,
      description: accordionItem.description,
      titleAlign,
      orientation,
      analyticsName,
      suffix: accordionItem.suffix,
      eyebrowText: accordionItem.eyebrowText,
      defaultOpen
    },
    accordionItem.title
  )), accordion = accordionItems.length > 1 ? /* @__PURE__ */ jsx(AccordionTamagui, { overflow: "hidden", defaultValue: multipleValue, type: "multiple", children: accordionItems }) : /* @__PURE__ */ jsx(AccordionTamagui, { overflow: "hidden", defaultValue: singleValue, type: "single", collapsible: !0, children: accordionItems });
  return analyticsName && trackImpression ? /* @__PURE__ */ jsx(
    ComponentWithViewTracking,
    {
      Component: accordion,
      analyticsName,
      eventName: "accordionViewEvent",
      inViewThreshold,
      ref
    }
  ) : accordion;
}), AccordionItem = ({
  showDivider,
  isFullWidth,
  disabled,
  title,
  description,
  size,
  variant,
  titleAlign,
  orientation,
  analyticsName,
  suffix,
  eyebrowText,
  defaultOpen
}) => {
  const showSuffix = !isFullWidth && suffix, { trackJourneyEvent } = useObservabilitySafe(), [isOpen, setIsOpen] = useState(defaultOpen ?? !1);
  let triggerColor = "$contentPrimary";
  disabled && (triggerColor = accordionTokens.color.content.state.inactive);
  const handlePress = () => {
    setIsOpen((prevIsOpen) => !prevIsOpen), analyticsName && trackJourneyEvent("platform", "accordionClickEvent", { targetName: analyticsName, accordionName: title });
  };
  return /* @__PURE__ */ jsxs(
    AccordionTamagui.Item,
    {
      value: title,
      "data-testid": "accordionTamagui-item",
      flexDirection: orientation === "top" ? "column" : "column-reverse",
      borderBottomWidth: showDivider ? "$default" : 0,
      borderBottomColor: showDivider ? "$borderPrimary" : "unset",
      children: [
        /* @__PURE__ */ jsxs(
          AccordionTamagui.Trigger,
          {
            height: accordionTokens.size.height[size],
            gap: accordionTokens.padding.betweenEyebrowAndValueText[size],
            justifyContent: "center",
            backgroundColor: "$transparent",
            borderWidth: 0,
            onPress: handlePress,
            focusVisibleStyle: {
              // Make sure all of the outline is visible, even with overflow="hidden"
              outlineOffset: -1
            },
            paddingHorizontal: 0,
            disabled,
            pointerEvents: disabled ? "none" : "auto",
            children: [
              eyebrowText && /* @__PURE__ */ jsx(Box, { alignItems: titleAlign === "right" ? "flex-end" : "flex-start", paddingRight: "$12x", children: /* @__PURE__ */ jsx(
                Text,
                {
                  tag: "span",
                  typography: accordionTokens.typography.eyebrow.default,
                  fontWeight: accordionTokens.fontWeight.eyebrowOverride,
                  color: accordionTokens.color.content.eyebrow,
                  children: eyebrowText
                }
              ) }),
              /* @__PURE__ */ jsxs(
                Box,
                {
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: isFullWidth ? "space-between" : titleAlign === "right" ? "flex-end" : "flex-start",
                  columnGap: accordionTokens.padding.betweenTextChevron.default,
                  children: [
                    /* @__PURE__ */ jsx(
                      Text,
                      {
                        tag: "span",
                        typography: accordionTokens.typography[size][variant],
                        fontWeight: variant === "regular" ? accordionTokens.fontWeight.valueTextOverride : void 0,
                        color: triggerColor,
                        textAlign: "left",
                        children: title
                      }
                    ),
                    /* @__PURE__ */ jsx(Box, { rotate: isOpen ? "180deg" : "0deg", animation: "accelerateDecelerateFastest", children: /* @__PURE__ */ jsx(ChevronDown, { size: 20, color: triggerColor }) }),
                    showSuffix && /* @__PURE__ */ jsx(Box, { flexGrow: 1, children: suffix })
                  ]
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsx(HeightAnimator, { isOpen, children: /* @__PURE__ */ jsx(
          AccordionTamagui.Content,
          {
            enterStyle: { opacity: 0 },
            exitStyle: { opacity: 0 },
            animation: "accelerateDecelerateFastest",
            paddingBottom: accordionTokens.padding.betweenContentAndDivider[size],
            children: typeof description == "string" ? /* @__PURE__ */ jsx(Text, { tag: "p", typography: "$bodyMedium", children: description }) : description
          }
        ) })
      ]
    }
  );
};
export {
  Accordion
};
